import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { apiErrorMessage } from '../../lib/api';
import { formatTimestampDateTime } from '../../lib/dates';
import {
  getWikiRevision,
  listWikiRevisions,
  type WikiPageFull,
  type WikiRevisionFull,
  type WikiRevisionSlim,
  type WikiSpaceRef,
} from '../../lib/wiki';
import { cn } from '../../lib/utils';
import { MemberByline } from './MemberByline';
import { useMembers } from './use-member-names';
import WikiMarkdownView from './WikiMarkdownView';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  space: WikiSpaceRef;
  page: WikiPageFull;
  /** Opens edit mode pre-filled with the revision. Nothing changes until Update. */
  onRestore: (revision: WikiRevisionFull) => void;
};

/** Screen 3b. */
export default function HistorySheet({ open, onOpenChange, space, page, onRestore }: Props) {
  const members = useMembers();
  const [items, setItems] = useState<WikiRevisionSlim[] | null>(null);
  const [selected, setSelected] = useState<WikiRevisionFull | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setSelected(null);
    setError(null);
    listWikiRevisions(space, page.id)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Failed to load history.'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, space, page.id, page.version]);

  async function show(version: number) {
    setError(null);
    try {
      setSelected(await getWikiRevision(space, page.id, version));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load revision.'));
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>History · {page.title}</SheetTitle>
          <SheetDescription>Every Update is a version. Restore loads one into the editor.</SheetDescription>
        </SheetHeader>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <ul className="max-h-56 divide-y overflow-y-auto rounded-md border text-sm">
          {items === null ? (
            <li className="px-3 py-2 text-muted-foreground">Loading…</li>
          ) : (
            items.map((row) => (
              <li key={row.version}>
                <button
                  type="button"
                  className={cn(
                    'grid w-full grid-cols-[3rem_1fr_auto] items-center gap-2 px-3 py-2 text-left hover:bg-accent/60',
                    selected?.version === row.version && 'bg-accent',
                  )}
                  onClick={() => void show(row.version)}
                >
                  <span className="font-mono text-xs">v{row.version}</span>
                  <MemberByline members={members} userId={row.author_id} size={18} />
                  <span className="text-xs text-muted-foreground">
                    {formatTimestampDateTime(row.created_at) ?? row.created_at}
                    {row.version === page.version ? ' · current' : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        {selected ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
              <span>v{selected.version}</span>
              <span aria-hidden>·</span>
              <MemberByline members={members} userId={selected.author_id} size={16} />
              <span aria-hidden>·</span>
              <span>{formatTimestampDateTime(selected.created_at) ?? selected.created_at}</span>
            </p>
            <p className="text-base font-semibold">{selected.title}</p>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border p-3">
              <WikiMarkdownView key={selected.version} value={selected.body_md} />
            </div>
            {selected.version !== page.version ? (
              <div className="flex justify-end">
                <Button type="button" size="sm" onClick={() => onRestore(selected)}>
                  Restore into editor
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
